import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  NativeContentsSuppressionCoordinator,
  type NativeContentsSuppressionPreparation,
} from './native-contents-suppression-coordinator';

interface NativeContentsSuppressionContextValue {
  suppressed: boolean;
  prepareSuppression: () => Promise<void>;
  registerPreparation: (
    prepare: NativeContentsSuppressionPreparation,
  ) => () => void;
}

const NativeContentsSuppressionContext =
  createContext<NativeContentsSuppressionContextValue>({
    suppressed: false,
    prepareSuppression: () => Promise.resolve(),
    registerPreparation: () => () => undefined,
  });

export interface NativeContentsSuppressionProviderProps {
  children: ReactNode;
  suppressed: boolean;
}

/**
 * Shares host-level native-content suppression within the renderer platform.
 * NativeContentsLayer combines this signal with geometric overlap detection
 * before swapping to a snapshot.
 */
export function NativeContentsSuppressionProvider({
  children,
  suppressed,
}: NativeContentsSuppressionProviderProps): ReactNode {
  const coordinatorRef = useRef(new NativeContentsSuppressionCoordinator());
  const registerPreparation = useCallback(
    (prepare: NativeContentsSuppressionPreparation): (() => void) =>
      coordinatorRef.current.register(prepare),
    [],
  );
  const prepareSuppression = useCallback(
    (): Promise<void> => coordinatorRef.current.prepare(),
    [],
  );
  const value = useMemo<NativeContentsSuppressionContextValue>(
    () => ({ prepareSuppression, registerPreparation, suppressed }),
    [prepareSuppression, registerPreparation, suppressed],
  );

  return (
    <NativeContentsSuppressionContext.Provider value={value}>
      {children}
    </NativeContentsSuppressionContext.Provider>
  );
}

/** Read the nearest host-level native-content suppression signal. */
// eslint-disable-next-line react-refresh/only-export-components
export function useNativeContentsSuppression(): boolean {
  return useContext(NativeContentsSuppressionContext).suppressed;
}

/** Prepare every native layer before a host paints a blocking DOM overlay. */
// eslint-disable-next-line react-refresh/only-export-components
export function usePrepareNativeContentsSuppression(): () => Promise<void> {
  return useContext(NativeContentsSuppressionContext).prepareSuppression;
}

/** Register one native layer in the nearest host's suppression handshake. */
// eslint-disable-next-line react-refresh/only-export-components
export function useNativeContentsSuppressionPreparation(
  prepare: NativeContentsSuppressionPreparation,
): void {
  const { registerPreparation } = useContext(NativeContentsSuppressionContext);
  useEffect(() => registerPreparation(prepare), [prepare, registerPreparation]);
}
